import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Clapperboard,
  Download,
  Film,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDuration(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s - m * 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

/**
 * R2-Phase-2.5 — Final Demo Video panel.
 * Self-sufficient: queries narration + voiceover state internally so the
 * parent page doesn't have to thread props.
 */
export default function FinalVideoPanel({ runId }) {
  const { hasPermission } = useAuth();
  const canStitch = hasPermission("workflow.run");
  const queryClient = useQueryClient();

  // Lightweight existence checks — both 404 cleanly when missing.
  const narration = useQuery({
    queryKey: ["narration-latest-for-final", runId],
    queryFn: async () => {
      try {
        return (await api.get(`/demo-runs/${runId}/narration`)).data;
      } catch (e) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!runId,
  });
  const voiceover = useQuery({
    queryKey: ["voiceover-latest", runId],   // shared cache key with VoiceoverPanel
    queryFn: async () => {
      try {
        return (await api.get(`/demo-runs/${runId}/voiceover`)).data;
      } catch (e) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!runId,
  });
  const hasNarration = !!(narration.data && narration.data.status === "ready");
  const hasVoiceover = !!voiceover.data;

  const final = useQuery({
    queryKey: ["final-video", runId],
    queryFn: async () => {
      try {
        return (await api.get(`/demo-runs/${runId}/final-video`)).data;
      } catch (e) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!runId,
  });

  // Poll while stitching is in progress.
  const [polling, setPolling] = useState(false);
  useEffect(() => {
    if (!polling) return undefined;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["final-video", runId] });
    }, 4000);
    return () => clearInterval(interval);
  }, [polling, queryClient, runId]);
  useEffect(() => {
    if (polling && final.data?.asset_id) {
      setPolling(false);
      toast.success("Final video ready");
    }
  }, [polling, final.data]);

  // Authed blob URL for the <video> element
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobLoading, setBlobLoading] = useState(false);
  useEffect(() => {
    let active = true;
    let toRevoke = null;
    async function load() {
      if (!final.data?.asset_id) return;
      setBlobLoading(true);
      try {
        const r = await api.get(
          `/demo-runs/${runId}/artifacts/${final.data.asset_id}`,
          { responseType: "blob" },
        );
        if (!active) return;
        const url = window.URL.createObjectURL(r.data);
        toRevoke = url;
        setBlobUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        if (active) toast.error(formatApiError(err));
      } finally {
        if (active) setBlobLoading(false);
      }
    }
    load();
    return () => {
      active = false;
      if (toRevoke) window.URL.revokeObjectURL(toRevoke);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [final.data?.asset_id, runId]);

  const stitch = useMutation({
    mutationFn: async ({ regenerate }) => {
      const body = { regenerate: !!regenerate };
      const r = await api.post(`/demo-runs/${runId}/stitch-video`, body);
      return r.data;
    },
    onSuccess: () => {
      setPolling(true);
      toast.message("Stitching the final video — ~30 seconds…", {
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["final-video", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const downloadBlob = useMutation({
    mutationFn: async () => {
      const r = await api.get(
        `/demo-runs/${runId}/artifacts/${final.data.asset_id}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eyvo_demo_${runId}.mp4`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const stitching = polling || stitch.isPending;
  const canStitchNow = hasNarration && hasVoiceover && canStitch;

  return (
    <section
      className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white p-5 shadow-sm"
      data-testid="final-video-panel"
    >
      <header className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Clapperboard size={18} className="text-indigo-600" />
            Final Demo Video
          </h2>
          <p className="text-xs text-slate-500">
            The recorded run combined with the latest narration audio into a
            single shareable MP4.
          </p>
        </div>
        {final.data && (
          <span className="text-[10px] text-slate-400">
            {fmtDuration(final.data.final_duration_s)} ·{" "}
            {fmtBytes(final.data.size_bytes)}
          </span>
        )}
      </header>

      {final.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : stitching && !final.data ? (
        <div
          className="flex items-center gap-2 py-4 text-sm text-indigo-700"
          data-testid="final-video-stitching"
        >
          <Loader2 size={14} className="animate-spin" />
          Stitching the final video with ffmpeg — typically ~30 seconds…
        </div>
      ) : !final.data ? (
        <div className="space-y-3" data-testid="final-video-empty">
          <div className="rounded-md border border-slate-200 bg-white/60 p-3 text-xs text-slate-600">
            <Film size={14} className="mr-2 inline text-slate-500" />
            No final video yet for this run. Click <strong>Generate Final
            Video</strong> to combine the raw recording + the voiceover into
            an MP4 you can download and share.
          </div>
          <Button
            size="sm"
            onClick={() => stitch.mutate({ regenerate: false })}
            disabled={!canStitchNow || stitch.isPending}
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
            data-testid="final-video-stitch-btn"
            title={
              canStitchNow
                ? "Combine raw recording + voiceover into a single MP4"
                : "Generate the narration script and voiceover first."
            }
          >
            {stitch.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Sparkles size={13} />
            )}
            Generate Final Video
          </Button>
          {!hasNarration && (
            <p className="text-[11px] text-slate-400">
              Step 1: generate the narration script.
            </p>
          )}
          {hasNarration && !hasVoiceover && (
            <p className="text-[11px] text-slate-400">
              Step 2: generate the voiceover audio.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="final-video-ready">
          {final.data.stale && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800"
              data-testid="final-video-stale-banner"
            >
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                Final video is out of date — the narration or voiceover has
                been updated since this MP4 was produced. Regenerate to refresh.
              </span>
            </div>
          )}
          {blobLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Loading video…
            </div>
          ) : blobUrl ? (
            <video
              controls
              src={blobUrl}
              preload="metadata"
              className="w-full rounded-lg border border-slate-200 bg-black"
              data-testid="final-video-player"
            >
              Your browser doesn&apos;t support HTML5 video.
            </video>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500">
            <span data-testid="final-video-meta">
              {final.data.provider || "—"} ·{" "}
              {final.data.voice_label || final.data.voice_id || "—"} ·{" "}
              video {fmtDuration(final.data.source_video_duration_s)} · audio{" "}
              {fmtDuration(final.data.audio_duration_s)} ·{" "}
              {final.data.audio_overrun_s > 0.05
                ? `audio overruns by ${final.data.audio_overrun_s}s (last frame holds)`
                : "video and audio aligned"}
              {final.data.ffmpeg_elapsed_ms != null && (
                <> · stitched in {Math.round(final.data.ffmpeg_elapsed_ms / 100) / 10}s</>
              )}
              {" · narration v"}{final.data.narration_version}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px]"
                onClick={() => downloadBlob.mutate()}
                disabled={downloadBlob.isPending}
                data-testid="final-video-download"
              >
                {downloadBlob.isPending ? (
                  <Loader2 size={11} className="mr-1 animate-spin" />
                ) : (
                  <Download size={11} className="mr-1" />
                )}
                Download MP4
              </Button>
              {canStitch && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => stitch.mutate({ regenerate: true })}
                  disabled={stitch.isPending}
                  data-testid="final-video-regenerate"
                >
                  {stitch.isPending ? (
                    <Loader2 size={11} className="mr-1 animate-spin" />
                  ) : (
                    <RefreshCw size={11} className="mr-1" />
                  )}
                  Regenerate
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
